/*
 * Copyright (c) 2025 NoorWays. All rights reserved.
 * Licensed under the terms specified in the LICENSE.txt file.
 */

import { logger } from '@/services/logger'
import { type MessageLanguages } from '@/i18n'
import en from '@/locales/en.json'
import fr from '@/locales/fr.json'
import es from '@/locales/es.json'

/**
 * Email utility functions for handling mailto links and email-related operations
 */

export interface MailtoOptions {
  to: string
  subject?: string
  body?: string
  cc?: string
  bcc?: string
}

/**
 * Internationalized email template structure
 */
export interface I18nEmailTemplate {
  to: string
  subjectKey: string
  bodyKey: string
}

/**
 * Email template with resolved translations
 */
export interface ResolvedEmailTemplate {
  to: string
  subject: string
  body: string
}

/**
 * Supported locales for email templates
 */
export type SupportedLocale = MessageLanguages

/**
 * Creates a properly formatted mailto URL with encoded parameters
 * @param options - Email options including recipient, subject, body, etc.
 * @returns Properly formatted and encoded mailto URL
 */
export function createMailtoUrl(options: MailtoOptions): string {
  const { to, subject, body, cc, bcc } = options
  
  // Validate email address
  if (!to || !isValidEmail(to)) {
    throw new Error('Valid recipient email address is required')
  }
  
  const params = new URLSearchParams()
  
  if (subject) {
    params.append('subject', subject)
  }
  
  if (body) {
    params.append('body', body)
  }
  
  if (cc) {
    params.append('cc', cc)
  }
  
  if (bcc) {
    params.append('bcc', bcc)
  }
  
  const queryString = params.toString()
  return `mailto:${to}${queryString ? `?${queryString}` : ''}`
}

/**
 * Opens an email client with the specified options
 * This function handles cross-browser and cross-platform compatibility
 * @param options - Email options including recipient, subject, body, etc.
 * @returns Promise that resolves when the email client is opened or rejects if it fails
 */
export function openEmailClient(options: MailtoOptions): Promise<void> {
  return new Promise((resolve, reject) => {
    try {
      const mailtoUrl = createMailtoUrl(options)

      // Use window.location.href for better compatibility
      // This approach works better than window.open() for mailto links
      window.location.href = mailtoUrl
      
      // Since we can't reliably detect if the email client opened successfully,
      // we'll resolve after a short delay
      setTimeout(() => {
        resolve()
      }, 100)
      
    } catch (error) {
      logger.error('Failed to open email client', error instanceof Error ? error : undefined, {
        to: options.to,
        subject: options.subject
      }, 'emailUtils')
      reject(error)
    }
  })
}

/**
 * Creates a fallback email client opener that tries multiple methods
 * @param options - Email options
 * @returns Promise that resolves when email client is opened
 */
export function openEmailClientWithFallback(options: MailtoOptions): Promise<void> {
  return new Promise((resolve, reject) => {
    try {
      const mailtoUrl = createMailtoUrl(options)
      
      // Method 1: Try window.location.href (most compatible)
      try {
        window.location.href = mailtoUrl
        setTimeout(() => resolve(), 100)
        return
      } catch (error) {
        logger.warn('Method 1 (window.location.href) failed, trying fallback', error instanceof Error ? { error: error.message, stack: error.stack } : undefined, 'emailUtils')
      }
      
      // Method 2: Try creating a temporary link and clicking it
      try {
        const link = document.createElement('a')
        link.href = mailtoUrl
        link.style.display = 'none'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        setTimeout(() => resolve(), 100)
        return
      } catch (error) {
        logger.warn('Method 2 (temporary link) failed', error instanceof Error ? { error: error.message, stack: error.stack } : undefined, 'emailUtils')
      }
      
      // If all methods fail, reject
      reject(new Error('All email client opening methods failed'))
      
    } catch (error) {
      reject(error)
    }
  })
}

/**
 * Basic email validation
 * @param email - Email address to validate
 * @returns True if email appears to be valid
 */
export function isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return emailRegex.test(email)
}

/**
 * Internationalized email templates for common support scenarios
 */
export const I18N_EMAIL_TEMPLATES = {
  EXTENSION_SUPPORT: {
    to: 'contact@noorways.fr',
    subjectKey: 'email.templates.extensionSupport.subject',
    bodyKey: 'email.templates.extensionSupport.body'
  },
  SUBSCRIPTION_ISSUE: {
    to: 'contact@noorways.fr',
    subjectKey: 'email.templates.subscriptionIssue.subject',
    bodyKey: 'email.templates.subscriptionIssue.body'
  },
  GENERAL_SUPPORT: {
    to: 'contact@noorways.fr',
    subjectKey: 'email.templates.generalSupport.subject',
    bodyKey: 'email.templates.generalSupport.body'
  },
  SUBSCRIPTION_QUESTION: {
    to: 'contact@noorways.fr',
    subjectKey: 'email.templates.subscriptionQuestion.subject',
    bodyKey: 'email.templates.subscriptionQuestion.body'
  }
} as const satisfies Record<string, I18nEmailTemplate>

/**
 * Resolves an internationalized email template to a concrete template with translations
 * @param templateKey - Key of the internationalized template
 * @param locale - Locale to use for translations (defaults to current i18n locale)
 * @returns Resolved email template with translated content
 */
/**
 * Type for nested object property access
 */
type NestedObject = Record<string, unknown>

/**
 * Get nested property from object using dot notation
 */
function getNestedProperty(obj: NestedObject, path: string): string {
  const result = path.split('.').reduce((current, key) => {
    return current && typeof current === 'object' && key in current
      ? (current as NestedObject)[key]
      : undefined
  }, obj as unknown)
  return typeof result === 'string' ? result : path
}

export function resolveEmailTemplate(
  templateKey: keyof typeof I18N_EMAIL_TEMPLATES,
  locale?: SupportedLocale
): ResolvedEmailTemplate {
  const template = I18N_EMAIL_TEMPLATES[templateKey]
  const currentLocale = locale || 'en'

  // Get the appropriate message object
  const messages = { en, fr, es }
  const localeMessages = messages[currentLocale] || messages.en

  // Get translations using direct property access
  const subject = getNestedProperty(localeMessages, template.subjectKey)
  const body = getNestedProperty(localeMessages, template.bodyKey)

  return {
    to: template.to,
    subject,
    body
  }
}

/**
 * Opens email client with an internationalized template
 * @param templateKey - Key of the internationalized template
 * @param locale - Locale to use for translations (defaults to current i18n locale)
 * @param customizations - Optional customizations to override template values
 * @returns Promise that resolves when email client is opened
 */
export function openEmailWithI18nTemplate(
  templateKey: keyof typeof I18N_EMAIL_TEMPLATES,
  locale?: SupportedLocale,
  customizations?: Partial<MailtoOptions>
): Promise<void> {
  try {
    const resolvedTemplate = resolveEmailTemplate(templateKey, locale)
    const options = { ...resolvedTemplate, ...customizations }
    return openEmailClientWithFallback(options)
  } catch (error) {
    logger.error('Failed to open email with i18n template', error instanceof Error ? error : undefined, {
      templateKey,
      locale,
      customizations
    }, 'emailUtils')
    throw error
  }
}


